import cx from 'classnames';
import { IDashboardLearnerInfo } from 'components/admin/dashboard/types';
import { Form, IconButton, Table } from 'components/admin/ui';
import { useAuthContext } from 'components/auth';
import { MinusCircle } from 'ethena-icons';
import { prettyPrintCustomCategoryLabel } from 'lib/helpers/prettyPrint';
import { ILocation } from 'lib/types/user';
import _ from 'lodash';
import { MouseEvent } from 'react';
import css from './PopulationTable.module.scss';

interface IPopulationTableProps {
  className?: string;
  getIsSelected?: (userId: number) => boolean;
  headClassName?: string;
  isBorderless?: boolean;
  onClick?: (userId: number) => void;
  onRemove?: (userId: number) => void;
  teamMembers: IDashboardLearnerInfo[];
}

export const PopulationTable = ({
  className,
  getIsSelected,
  isBorderless = false,
  onClick,
  onRemove,
  teamMembers,
}: IPopulationTableProps) => {
  const { session } = useAuthContext();

  return (
    <Table.Table className={cx(className, { [css.borderless]: isBorderless })}>
      <Table.Head className={css.tableHead}>
        <Table.Row>
          <Table.Cell width={200}>Name</Table.Cell>
          <Table.Cell width={60}>Location</Table.Cell>
          <Table.Cell width={120}>Department</Table.Cell>
          <Table.Cell className="text-center" width={1}>
            Manager
          </Table.Cell>
          <Table.Cell width={70}>Worker Type</Table.Cell>
          <Table.Cell width={120}>
            {prettyPrintCustomCategoryLabel(
              session.company.customCategoryLabel
            )}
          </Table.Cell>
          {onRemove && <Table.Cell />}
        </Table.Row>
      </Table.Head>

      <Table.Body>
        {_.map(teamMembers, (teamMember) => (
          <PopulationTableRow
            key={teamMember.userId}
            isSelected={getIsSelected?.(teamMember.userId)}
            teamMember={teamMember}
            onClick={onClick ? () => onClick(teamMember.userId) : undefined}
            onRemove={onRemove ? () => onRemove(teamMember.userId) : undefined}
          />
        ))}
      </Table.Body>
    </Table.Table>
  );
};

interface IPopulationTableRowProps {
  isSelected?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  teamMember: IDashboardLearnerInfo;
}

const PopulationTableRow = ({
  isSelected = false,
  onClick,
  onRemove,
  teamMember,
}: IPopulationTableRowProps) => (
  <Table.Row
    className={cx(css.tableRow, {
      [css.selected]: isSelected,
      [css.defaultCursor]: !onClick,
    })}
    onClick={(ev: MouseEvent) => {
      ev.preventDefault();
      onClick?.();
    }}
  >
    <Table.Cell className={css.name}>
      {onClick ? (
        <Form.Checkbox
          checked={isSelected}
          label={teamMember.name}
          onChange={onClick}
        />
      ) : (
        teamMember.name
      )}
    </Table.Cell>
    <Table.Cell className={css.location}>
      {renderLocation(teamMember.location)}
    </Table.Cell>
    <Table.Cell className={css.department}>
      {teamMember.department || <>&mdash;</>}
    </Table.Cell>
    <Table.Cell className="text-center">
      <span
        className={cx(css.managerTag, {
          [css.isManager]: teamMember.isManager,
        })}
      />
    </Table.Cell>
    <Table.Cell className={css.employeeType}>
      {teamMember.employeeType || <>&mdash;</>}
    </Table.Cell>
    <Table.Cell className={css.customCategory}>
      {teamMember.customCategory || <>&mdash;</>}
    </Table.Cell>

    {onRemove && (
      <Table.Cell>
        <IconButton color="red" icon={MinusCircle} onClick={onRemove} />
      </Table.Cell>
    )}
  </Table.Row>
);

const renderLocation = (location: ILocation) =>
  `${location.country}${location.state ? ` - ${location.state}` : ''}`;
